import { useEffect, useRef, useState } from 'react';
import { getSpineManager } from '../../lib/spine-manager';
import { useSpineLoader } from '../../hooks/useSpineLoader';
import { useSceneStore } from '../../stores/sceneStore';
import type { Layer, AnimationTrack } from '@spine-workspace/shared';

interface SpineLayerRendererProps {
  layer: Layer;
}

export default function SpineLayerRenderer({ layer }: SpineLayerRendererProps) {
  const { loadAsset } = useSpineLoader();
  const loaded = useRef(false);
  const [missingTextures, setMissingTextures] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Mount / unmount — load asset and add spine to scene
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    async function load() {
      const manager = getSpineManager();
      await manager.waitForInit();
      if (cancelled) return;

      const ok = await loadAsset(layer.assetId);
      if (!ok || cancelled) { setIsLoading(false); return; }

      if (getSpineManager().hasMissingTextures(layer.assetId)) {
        setMissingTextures(true);
      }

      const spine = manager.addSpineToScene(layer.id, layer.assetId, layer.transform);
      if (!spine) { setIsLoading(false); return; }

      loaded.current = true;
      setIsLoading(false);

      manager.setLayerVisible(layer.id, layer.visible);
      manager.setLayerOpacity(layer.id, layer.opacity);
      if (layer.blendMode && layer.blendMode !== 'normal') {
        manager.setLayerBlendMode(layer.id, layer.blendMode);
      }
      if (layer.effects) {
        manager.setLayerEffects(layer.id, layer.effects.brightness, layer.effects.saturation, layer.effects.tint);
      }
      manager.setSkin(layer.id, layer.skin);

      // Apply stored tracks; if none exist, auto-add T0 with the first available animation
      if (layer.animation.tracks.length > 0) {
        for (const track of layer.animation.tracks) {
          manager.setAnimation(layer.id, track.trackIndex, track.name, track.loop, track.timeScale);
        }
      } else {
        const availableAnims = manager.getAnimationNames(layer.id);
        if (availableAnims.length > 0) {
          const firstName = availableAnims[0];
          const newTrack: AnimationTrack = { trackIndex: 0, name: firstName, loop: true, timeScale: 1 };
          useSceneStore.getState().setLayerAnimationTracks(layer.id, [newTrack]);
          manager.setAnimation(layer.id, 0, firstName, true, 1);
        }
      }

      if (layer.ikPose) {
        manager.applyIkPose(layer.id, layer.ikPose);
      }
      if (layer.physicsConstraintPose) {
        manager.applyPhysicsPose(layer.id, layer.physicsConstraintPose);
      }
    }

    load();

    return () => {
      cancelled = true;
      if (loaded.current) {
        const { assetId } = layer;
        getSpineManager().removeSpineFromScene(layer.id);
        const remainingLayers = useSceneStore.getState().layers;
        const stillUsed = remainingLayers.some((l) => l.assetId === assetId);
        if (!stillUsed) {
          getSpineManager().unloadAsset(assetId);
        }
        loaded.current = false;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.id, layer.assetId]);

  // Transform updates
  useEffect(() => {
    if (!loaded.current) return;
    getSpineManager().updateTransform(layer.id, layer.transform);
  }, [layer.id, layer.transform]);

  // Visual properties: visibility, opacity, blend mode, effects
  useEffect(() => {
    if (!loaded.current) return;
    const manager = getSpineManager();
    manager.setLayerVisible(layer.id, layer.visible);
    manager.setLayerOpacity(layer.id, layer.opacity);
    manager.setLayerBlendMode(layer.id, layer.blendMode ?? 'normal');
    const e = layer.effects;
    manager.setLayerEffects(layer.id, e?.brightness ?? 1, e?.saturation ?? 0, e?.tint ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.id, layer.visible, layer.opacity, layer.blendMode, layer.effects]);

  // Animation / skin / IK / physics
  useEffect(() => {
    if (!loaded.current) return;
    const manager = getSpineManager();
    manager.setSkin(layer.id, layer.skin);
    for (const track of layer.animation.tracks) {
      manager.setAnimation(layer.id, track.trackIndex, track.name, track.loop, track.timeScale);
    }
    if (layer.ikPose) manager.applyIkPose(layer.id, layer.ikPose);
    if (layer.physicsConstraintPose) manager.applyPhysicsPose(layer.id, layer.physicsConstraintPose);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.id, layer.skin, layer.animation.tracks, layer.ikPose, layer.physicsConstraintPose]);

  if (!isLoading && !missingTextures) return null;

  return (
    <>
      {isLoading && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none flex items-center gap-2 bg-black/50 rounded-lg px-3 py-2">
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          <span className="text-xs text-white/70">Loading…</span>
        </div>
      )}
      {/* Overlay badge for missing textures */}
      {missingTextures && (
        <div className="absolute top-8 left-1/2 -translate-x-1/2 z-20 pointer-events-none flex items-center gap-1 bg-orange-900/90 border border-orange-500/60 rounded px-2 py-0.5">
          <span className="text-[9px] font-mono font-bold text-orange-400 uppercase tracking-wider">Missing images</span>
          <span className="text-[9px] font-sans text-orange-300">— skeleton visible with placeholders</span>
        </div>
      )}
    </>
  );
}
